import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, AlertTriangle, ArrowLeft, XCircle, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CancelPage() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";

  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [info, setInfo] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");

  useEffect(() => {
    if (!token) {
      setErrorMsg("Missing cancellation token in link.");
      setStatus("error");
      return;
    }
    axios
      .get(`${API}/booking/cancel-info/${bookingId}`, { params: { t: token } })
      .then((res) => {
        setInfo(res.data);
        setStatus("ready");
      })
      .catch((err) => {
        setErrorMsg(err.response?.data?.detail || "This cancellation link is no longer valid.");
        setStatus("error");
      });
  }, [bookingId, token]);

  const submit = async () => {
    const msg = info.free_rebook
      ? "Cancel this session? Your credit will be released but no refund is due."
      : info.within_grace_window
      ? `Cancel this session? You'll be refunded the full €${info.refund_eur.toFixed(2)} to your card.`
      : `Cancel this session? You'll be refunded €${info.refund_eur.toFixed(2)} to your card (a €${info.cancellation_fee_eur.toFixed(2)} late-cancellation fee applies inside the 24h window).`;
    if (!window.confirm(msg)) return;
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/booking/cancel/${bookingId}`, {
        t: token,
        reason: reason || null,
        reason_note: reasonNote || null,
      });
      setResult(res.data);
      setStatus("done");
      toast.success("Session cancelled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel the session");
    } finally {
      setConfirming(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md border border-red-500/30 bg-red-500/[0.05] p-8 text-center" data-testid="cancel-error">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-white mb-3">Cannot cancel</h1>
          <p className="text-slate-300 text-sm leading-relaxed mb-6">{errorMsg}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-5 py-2.5 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Bad Boss Clinic
          </a>
        </div>
      </div>
    );
  }

  if (status === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md border border-slate-800 bg-slate-900/70 p-8 text-center" data-testid="cancel-done">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-slate-700 bg-slate-950 mb-4">
            <Check className="w-7 h-7 text-slate-300" />
          </div>
          <h1 className="font-serif text-2xl text-white mb-3">Session cancelled</h1>
          {result?.refund_eur > 0 ? (
            <p className="text-slate-300 text-sm leading-relaxed mb-2">
              A refund of <strong className="text-[#D4AF37]">€{result.refund_eur.toFixed(2)}</strong> is on its way
              back to your card. Card issuers typically take 5–10 business days to display it.
            </p>
          ) : (
            <p className="text-slate-300 text-sm mb-2">
              No refund is due for this session because it was booked with a free-rebook credit.
            </p>
          )}
          {result?.cancellation_fee_eur > 0 && (
            <p className="text-slate-400 text-xs mb-6">Cancellation fee kept: €{result.cancellation_fee_eur.toFixed(2)}</p>
          )}
          <a
            href="/"
            className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-5 py-2.5 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Bad Boss Clinic
          </a>
        </div>
      </div>
    );
  }

  const isFree = info.free_rebook;
  return (
    <div className="min-h-screen py-12 px-4" data-testid="cancel-page">
      <div className="max-w-xl mx-auto">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-xs uppercase tracking-widest mb-6 transition-colors"
        >
          <ArrowLeft className="w-3 h-3" /> Bad Boss Clinic
        </a>

        <div className="border border-red-500/30 bg-slate-900/50 p-6 md:p-8">
          <p className="text-[10px] uppercase tracking-[0.24em] text-red-400 mb-2">Cancel session</p>
          <h1 className="font-serif text-3xl text-white mb-4">Confirm cancellation</h1>

          <div className="border-t border-slate-800 pt-4 space-y-4">
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">Session</p>
              <p className="text-white text-lg" data-testid="cancel-current-slot">
                {info.date} · {info.start_time} – {info.end_time} UTC
              </p>
              <p className="text-slate-500 text-sm capitalize">
                {info.session_type === "initial" ? "Initial consultation" : "Follow-up"} · booked by {info.client_name}
              </p>
            </div>

            <div className="border-t border-slate-800 pt-4" data-testid="cancel-refund-breakdown">
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-3">Refund breakdown</p>
              {isFree ? (
                <p className="text-slate-300 text-sm">
                  This session was booked with a free-rebook credit. Cancelling will release the slot with no refund
                  due.
                </p>
              ) : info.within_grace_window ? (
                <div className="space-y-2 text-sm" data-testid="cancel-full-refund">
                  <p className="text-green-400 text-xs uppercase tracking-widest">
                    ✓ More than {info.grace_hours}h out — full refund
                  </p>
                  <div className="flex justify-between border-t border-slate-800 pt-3 mt-3">
                    <span className="text-slate-300 font-medium">Refunded to your card</span>
                    <span className="text-[#D4AF37] font-mono text-lg" data-testid="cancel-refund-amount">
                      €{info.refund_eur.toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <p className="text-yellow-400 text-xs uppercase tracking-widest">
                    Late cancellation — inside the {info.grace_hours}h window
                  </p>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Session price</span>
                    <span className="text-white font-mono">€{info.price_eur.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Cancellation fee</span>
                    <span className="text-slate-300 font-mono">−€{info.cancellation_fee_eur.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-800 pt-3 mt-3">
                    <span className="text-slate-300 font-medium">Refunded to your card</span>
                    <span className="text-[#D4AF37] font-mono text-lg" data-testid="cancel-refund-amount">
                      €{info.refund_eur.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-800 space-y-3" data-testid="cancel-reason-picker">
            <p className="text-slate-400 text-xs uppercase tracking-widest">Reason (optional — helps us improve)</p>
            <div className="flex flex-wrap gap-2">
              {(info.reason_options || []).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setReason(reason === opt.value ? "" : opt.value)}
                  data-testid={`cancel-reason-${opt.value}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                    reason === opt.value
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "border-slate-700 text-slate-300 hover:border-[#D4AF37]/60"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {reason === "other" && (
              <textarea
                data-testid="cancel-reason-note"
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Anything you'd like to add"
                className="w-full bg-slate-950 border border-slate-800 focus:border-[#D4AF37]/60 outline-none text-slate-200 text-sm p-3"
              />
            )}
          </div>

          <div className="mt-8 pt-6 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <a
              href={`/reschedule/${bookingId}?t=${token}`}
              className="text-slate-400 text-xs uppercase tracking-widest hover:text-[#D4AF37] transition-colors"
              data-testid="cancel-reschedule-instead"
            >
              ← Reschedule instead
            </a>
            <button
              onClick={submit}
              disabled={confirming}
              data-testid="cancel-confirm-btn"
              className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500 text-red-400 hover:bg-red-500 hover:text-white px-6 py-3 text-xs uppercase tracking-widest font-medium disabled:opacity-50 transition-colors"
            >
              {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Confirm cancellation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
